import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AudioPlayer from '../../../components/AudioPlayer';

const IconicQuotesSection = ({ 
  quotes, 
  isGenerating, 
  onGenerate, 
  onLineClick,
  characterName,
  isAdmin
}) => {
  const [expandedQuote, setExpandedQuote] = useState(null);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Soliloquy':
        return 'MessageCircle';
      case 'Iconic Quote':
        return 'Star';
      case 'Signature Dialogue':
        return 'Quote';
      case 'Character Defining':
        return 'Sparkles';
      default:
        return 'MessageSquare';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Soliloquy':
        return 'text-purple-500 bg-purple-500/10';
      case 'Iconic Quote':
        return 'text-yellow-500 bg-yellow-500/10';
      case 'Signature Dialogue':
        return 'text-blue-500 bg-blue-500/10';
      case 'Character Defining':
        return 'text-pink-500 bg-pink-500/10';
      default:
        return 'text-primary bg-primary/10';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-card-foreground flex items-center gap-2">
          <Icon name="Quote" size={24} className="text-primary" />
          Iconic Quotes & Soliloquies
        </h2>
        {isAdmin && !quotes && (
          <Button
            onClick={onGenerate}
            disabled={isGenerating}
            iconName={isGenerating ? 'Loader2' : 'Sparkles'}
            className={isGenerating ? 'animate-spin' : ''}
          >
            {isGenerating ? 'Generating...' : 'Generate Quotes'}
          </Button>
        )}
        {isAdmin && quotes && quotes?.length > 0 && (
          <Button
            onClick={onGenerate}
            disabled={isGenerating}
            variant="outline"
            size="sm"
            iconName={isGenerating ? 'Loader2' : 'RefreshCw'}
            className={isGenerating ? 'animate-spin' : ''}
          >
            {isGenerating ? 'Regenerating...' : 'Regenerate'}
          </Button>
        )}
      </div>

      {quotes && quotes?.length > 0 ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground mb-6">
            Explore the most memorable lines that define <span className="font-semibold text-card-foreground">{characterName}</span>'s 
            voice, personality, and thematic contributions to the play.
          </p>

          <div className="grid gap-4">
            {quotes?.map((quote, index) => (
              <div
                key={index}
                className="bg-muted rounded-lg p-5 hover:bg-muted/80 transition-all duration-200 border border-transparent hover:border-primary/20"
              >
                {/* Category Badge */}
                <div className="flex items-center gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(quote?.category)}`}>
                    <Icon name={getCategoryIcon(quote?.category)} size={14} />
                    {quote?.category}
                  </span>
                  {quote?.act && quote?.scene && (
                    <span className="text-xs text-muted-foreground">
                      Act {quote?.act}, Scene {quote?.scene}
                    </span>
                  )}
                </div>

                {/* Quote Text */}
                <blockquote className="relative mb-4">
                  <Icon 
                    name="Quote" 
                    size={32} 
                    className="absolute -top-2 -left-1 text-primary/20" 
                  />
                  <p className="text-foreground text-base leading-8 tracking-normal pl-8 italic">
                    "{quote?.quote}"
                  </p>
                </blockquote>

                {/* Significance */}
                <div className="bg-card rounded-lg p-3 mb-3">
                  <p className="text-xs italic text-muted-foreground/70 tracking-wide">
                    <span className="font-extrabold not-italic text-card-foreground tracking-widest uppercase text-xs">Why it matters:</span> {quote?.significance}
                  </p>
                </div>

                {/* Audio Player and Actions */}
                <div className="flex items-center gap-3 flex-wrap">
                  {quote?.audioUrl ? (
                    <div className="flex-1 min-w-[250px]">
                      <AudioPlayer
                        audioUrl={quote?.audioUrl}
                        label="Play Quote"
                        size="sm"
                        variant="outline"
                        showDuration={true}
                        onPlayStart={() => {}}
                        onPlayEnd={() => {}}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Volume2" size={16} />
                      <span>Audio not available</span>
                    </div>
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onLineClick?.({
                      line_id: quote?.line_id,
                      id: quote?.lineId
                    })}
                    iconName="ExternalLink"
                    className="ml-auto"
                  >
                    View in Play
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : quotes && quotes?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Quote" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            No iconic quotes could be generated for this character
          </p>
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="Quote" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">
            {isAdmin 
              ? 'Click "Generate Quotes" to discover the most memorable lines that showcase this character\'s voice and thematic contributions' :'Iconic quotes not yet available for this character'
            }
          </p>
          {isAdmin && (
            <div className="flex items-center justify-center gap-6 mt-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="Star" size={16} className="text-yellow-500" />
                <span>Iconic Quotes</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="MessageCircle" size={16} className="text-purple-500" />
                <span>Soliloquies</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Quote" size={16} className="text-blue-500" />
                <span>Signature Dialogue</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IconicQuotesSection;